// News data storage - this would typically be a JSON file or database
// For now, we'll use localStorage as a simulation of file storage

export const newsStorage = {
  // Get all news items
  getAllNews: () => {
    try {
      const storedNews = localStorage.getItem('alasar_news');
      return storedNews ? JSON.parse(storedNews) : [];
    } catch (error) {
      console.error('Error loading news:', error);
      return [];
    }
  },

  // Save a single news item
  saveNews: (newsItem) => {
    try {
      const allNews = newsStorage.getAllNews();
      const newNewsItem = {
        id: newsItem.id || Date.now(),
        title: newsItem.title,
        content: newsItem.content,
        image: newsItem.image,
        date: newsItem.date || new Date().toISOString().split('T')[0],
        author: newsItem.author || 'Admin',
        category: newsItem.category || 'General',
        createdAt: newsItem.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // If updating existing item
      if (newsItem.id) {
        const index = allNews.findIndex(item => item.id === newsItem.id);
        if (index !== -1) {
          allNews[index] = newNewsItem;
        }
      } else {
        // Adding new item
        allNews.unshift(newNewsItem);
      }

      localStorage.setItem('alasar_news', JSON.stringify(allNews));
      return newNewsItem;
    } catch (error) {
      console.error('Error saving news:', error);
      throw error;
    }
  },

  // Delete a news item
  deleteNews: (id) => {
    try {
      const allNews = newsStorage.getAllNews();
      const filteredNews = allNews.filter(item => item.id !== id);
      localStorage.setItem('alasar_news', JSON.stringify(filteredNews));
      return true;
    } catch (error) {
      console.error('Error deleting news:', error);
      return false;
    }
  },

  // Get news by ID
  getNewsById: (id) => {
    try {
      const allNews = newsStorage.getAllNews();
      return allNews.find(item => item.id === id);
    } catch (error) {
      console.error('Error getting news by ID:', error);
      return null;
    }
  },

  // Export news data (for backup)
  exportNews: () => {
    try {
      const allNews = newsStorage.getAllNews();
      const dataStr = JSON.stringify(allNews, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `alasar-news-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Error exporting news:', error);
      return false;
    }
  },

  // Import news data (for restore)
  importNews: (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedData = JSON.parse(e.target.result);
          if (Array.isArray(importedData)) {
            localStorage.setItem('alasar_news', JSON.stringify(importedData));
            resolve(true);
          } else {
            reject(new Error('Invalid data format'));
          }
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  }
};

// Announcement storage utilities
export const announcementStorage = {
  // Get all announcements
  getAllAnnouncements: () => {
    try {
      const storedAnnouncements = localStorage.getItem('alasar_announcements');
      return storedAnnouncements ? JSON.parse(storedAnnouncements) : [];
    } catch (error) {
      console.error('Error loading announcements:', error);
      return [];
    }
  },

  // Save an announcement
  saveAnnouncement: (announcementItem) => {
    try {
      const allAnnouncements = announcementStorage.getAllAnnouncements();
      const newAnnouncementItem = {
        id: announcementItem.id || Date.now(),
        title: announcementItem.title,
        content: announcementItem.content,
        priority: announcementItem.priority || 'normal',
        date: announcementItem.date || new Date().toISOString().split('T')[0],
        author: announcementItem.author || 'Admin',
        createdAt: announcementItem.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // If updating existing item
      if (announcementItem.id) {
        const index = allAnnouncements.findIndex(item => item.id === announcementItem.id);
        if (index !== -1) {
          allAnnouncements[index] = newAnnouncementItem;
        }
      } else {
        // Adding new item
        allAnnouncements.unshift(newAnnouncementItem);
      }

      localStorage.setItem('alasar_announcements', JSON.stringify(allAnnouncements));
      return newAnnouncementItem;
    } catch (error) {
      console.error('Error saving announcement:', error);
      throw error;
    }
  },

  // Delete an announcement
  deleteAnnouncement: (id) => {
    try {
      const allAnnouncements = announcementStorage.getAllAnnouncements();
      const filteredAnnouncements = allAnnouncements.filter(item => item.id !== id);
      localStorage.setItem('alasar_announcements', JSON.stringify(filteredAnnouncements));
      return true;
    } catch (error) {
      console.error('Error deleting announcement:', error);
      return false;
    }
  },

  // Get announcement by ID
  getAnnouncementById: (id) => {
    try {
      const allAnnouncements = announcementStorage.getAllAnnouncements();
      return allAnnouncements.find(item => item.id === id);
    } catch (error) {
      console.error('Error getting announcement by ID:', error);
      return null;
    }
  }
};

// ===== IndexedDB Admission Storage =====
const ADMISSION_DB_NAME = 'AlasarAdmissionDB';
const ADMISSION_DB_VERSION = 1;
const ADMISSION_STORE_NAME = 'admissions';

// Initialize Admission IndexedDB
const initAdmissionDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(ADMISSION_DB_NAME, ADMISSION_DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(ADMISSION_STORE_NAME)) {
        db.createObjectStore(ADMISSION_STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

// Convert base64 to Blob
const base64ToBlobForAdmission = (base64String) => {
  const [header, data] = base64String.split(',');
  const mimeType = header.match(/:(.*?);/)[1];
  const bytes = atob(data);
  const byteArrays = [];
  
  for (let i = 0; i < bytes.length; i++) {
    byteArrays.push(bytes.charCodeAt(i));
  }
  
  return new Blob([new Uint8Array(byteArrays)], { type: mimeType });
};

// Convert Blob to data URL
const blobToDataURLForAdmission = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const admissionStorage = {
  // Get all admissions
  getAllAdmissions: async () => {
    try {
      const db = await initAdmissionDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ADMISSION_STORE_NAME], 'readonly');
        const store = transaction.objectStore(ADMISSION_STORE_NAME);
        const request = store.getAll();
        
        request.onsuccess = async () => {
          const admissions = request.result || [];
          // Convert Blobs to data URLs for display
          const admissionsWithUrls = await Promise.all(
            admissions.map(async (admission) => {
              if (admission.blob && admission.blob instanceof Blob) {
                const url = await blobToDataURLForAdmission(admission.blob);
                return { ...admission, imageUrl: url };
              }
              return admission;
            })
          );
          resolve(admissionsWithUrls);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error loading admissions from IndexedDB:', error);
      // Fallback to localStorage
      try {
        const storedAdmissions = localStorage.getItem('alasar_admissions');
        return storedAdmissions ? JSON.parse(storedAdmissions) : [];
      } catch (e) {
        return [];
      }
    }
  },

  // Save an admission
  saveAdmission: async (admissionItem) => {
    try {
      const db = await initAdmissionDB();
      const newAdmissionItem = {
        id: admissionItem.id || Date.now(),
        title: admissionItem.title,
        startDate: admissionItem.startDate,
        endDate: admissionItem.endDate,
        description: admissionItem.description,
        link: admissionItem.link || '#',
        status: admissionItem.status || 'Open',
        createdAt: admissionItem.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Handle image if provided
      if (admissionItem.image && admissionItem.image.startsWith('data:image')) {
        const blob = base64ToBlobForAdmission(admissionItem.image);
        newAdmissionItem.blob = blob;
        newAdmissionItem.imageUrl = admissionItem.image;
      } else if (admissionItem.imageUrl) {
        newAdmissionItem.imageUrl = admissionItem.imageUrl;
      }

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ADMISSION_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(ADMISSION_STORE_NAME);
        const request = store.put(newAdmissionItem);
        
        request.onsuccess = () => resolve(newAdmissionItem);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error saving admission to IndexedDB:', error);
      // Fallback to localStorage
      try {
        const allAdmissions = admissionStorage.getAllAdmissions();
        const newAdmissionItem = {
          id: admissionItem.id || Date.now(),
          title: admissionItem.title,
          startDate: admissionItem.startDate,
          endDate: admissionItem.endDate,
          description: admissionItem.description,
          link: admissionItem.link || '#',
          status: admissionItem.status || 'Open',
          createdAt: admissionItem.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          imageUrl: admissionItem.image || admissionItem.imageUrl
        };

        const all = Array.isArray(allAdmissions) ? allAdmissions : [];
        if (!admissionItem.id) {
          all.unshift(newAdmissionItem);
        } else {
          const index = all.findIndex(item => item.id === admissionItem.id);
          if (index !== -1) {
            all[index] = newAdmissionItem;
          } else {
            all.unshift(newAdmissionItem);
          }
        }

        localStorage.setItem('alasar_admissions', JSON.stringify(all));
        return newAdmissionItem;
      } catch (e) {
        throw error;
      }
    }
  },

  // Delete an admission
  deleteAdmission: async (id) => {
    try {
      const db = await initAdmissionDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ADMISSION_STORE_NAME], 'readwrite');
        const store = transaction.objectStore(ADMISSION_STORE_NAME);
        const request = store.delete(id);
        
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error deleting admission from IndexedDB:', error);
      // Fallback to localStorage
      try {
        const all = admissionStorage.getAllAdmissions();
        const filtered = Array.isArray(all) ? all.filter(item => item.id !== id) : [];
        localStorage.setItem('alasar_admissions', JSON.stringify(filtered));
        return true;
      } catch (e) {
        return false;
      }
    }
  },

  // Get admission by ID
  getAdmissionById: async (id) => {
    try {
      const db = await initAdmissionDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([ADMISSION_STORE_NAME], 'readonly');
        const store = transaction.objectStore(ADMISSION_STORE_NAME);
        const request = store.get(id);
        
        request.onsuccess = async () => {
          const admission = request.result;
          if (admission && admission.blob && admission.blob instanceof Blob) {
            const url = await blobToDataURLForAdmission(admission.blob);
            resolve({ ...admission, imageUrl: url });
          } else {
            resolve(admission);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting admission by ID from IndexedDB:', error);
      try {
        const all = admissionStorage.getAllAdmissions();
        return Array.isArray(all) ? all.find(item => item.id === id) : null;
      } catch (e) {
        return null;
      }
    }
  }
};

// ===== IndexedDB Image Storage =====
// Using IndexedDB for efficient image storage without backend

const DB_NAME = 'AlasarImageDB';
const DB_VERSION = 1;
const STORE_NAME = 'images';

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
};

// Convert base64 to Blob
const base64ToBlob = (base64String) => {
  const [header, data] = base64String.split(',');
  const mimeType = header.match(/:(.*?);/)[1];
  const bytes = atob(data);
  const byteArrays = [];
  
  for (let i = 0; i < bytes.length; i++) {
    byteArrays.push(bytes.charCodeAt(i));
  }
  
  return new Blob([new Uint8Array(byteArrays)], { type: mimeType });
};

// Convert Blob to data URL
const blobToDataURL = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export const imageStorage = {
  // Get all images
  getAllImages: async () => {
    try {
      const db = await initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.getAll();
        
        request.onsuccess = async () => {
          const images = request.result || [];
          // Convert Blobs to data URLs for display
          const imagesWithUrls = await Promise.all(
            images.map(async (image) => {
              if (image.blob && image.blob instanceof Blob) {
                const url = await blobToDataURL(image.blob);
                return { ...image, url };
              }
              return image;
            })
          );
          resolve(imagesWithUrls);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error loading images from IndexedDB:', error);
      // Fallback to localStorage if IndexedDB fails
      try {
        const storedImages = localStorage.getItem('alasar_images');
        return storedImages ? JSON.parse(storedImages) : [];
      } catch (e) {
        return [];
      }
    }
  },

  // Save an image
  saveImage: async (imageItem) => {
    try {
      const db = await initDB();
      const newImageItem = {
        id: imageItem.id || Date.now(),
        title: imageItem.title,
        category: imageItem.category,
        date: imageItem.date || new Date().toISOString().split('T')[0],
        createdAt: imageItem.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // If it's a base64 string, convert to Blob
      if (imageItem.url && imageItem.url.startsWith('data:image')) {
        const blob = base64ToBlob(imageItem.url);
        newImageItem.blob = blob;
        // Also save URL for quick access
        newImageItem.url = imageItem.url;
      } else if (imageItem.url) {
        // External URL
        newImageItem.url = imageItem.url;
        // Try to fetch and cache as blob
        try {
          const response = await fetch(imageItem.url);
          const blob = await response.blob();
          newImageItem.blob = blob;
        } catch (e) {
          console.log('Could not cache external image:', e);
        }
      }

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.put(newImageItem);
        
        request.onsuccess = () => resolve(newImageItem);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error saving image to IndexedDB:', error);
      // Fallback to localStorage
      try {
        const allImages = imageStorage.getAllImages();
        const newImageItem = {
          id: imageItem.id || Date.now(),
          title: imageItem.title,
          url: imageItem.url,
          category: imageItem.category,
          date: imageItem.date || new Date().toISOString().split('T')[0],
          createdAt: imageItem.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        if (!imageItem.id) {
          allImages.unshift(newImageItem);
        } else {
          const index = allImages.findIndex(item => item.id === imageItem.id);
          if (index !== -1) {
            allImages[index] = newImageItem;
          } else {
            allImages.unshift(newImageItem);
          }
        }

        localStorage.setItem('alasar_images', JSON.stringify(allImages));
        return newImageItem;
      } catch (e) {
        throw error;
      }
    }
  },

  // Delete an image
  deleteImage: async (id) => {
    try {
      const db = await initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.delete(id);
        
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error deleting image from IndexedDB:', error);
      // Fallback to localStorage
      try {
        const allImages = imageStorage.getAllImages();
        const filteredImages = allImages.filter(item => item.id !== id);
        localStorage.setItem('alasar_images', JSON.stringify(filteredImages));
        return true;
      } catch (e) {
        return false;
      }
    }
  },

  // Clear all images (for admin use)
  clearAllImages: async () => {
    try {
      const db = await initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        const request = store.clear();
        
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error clearing images:', error);
      return false;
    }
  }
};
